"use client";

import { getLocalTimeZone, today } from "@internationalized/date";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ComponentProps } from "react";
import {
  Button,
  CalendarCell as CalendarCellRac,
  CalendarGridBody as CalendarGridBodyRac,
  CalendarGridHeader as CalendarGridHeaderRac,
  CalendarGrid as CalendarGridRac,
  CalendarHeaderCell as CalendarHeaderCellRac,
  Calendar as CalendarRac,
  Heading as HeadingRac,
  RangeCalendar as RangeCalendarRac,
  composeRenderProps,
} from "react-aria-components";
import { cn } from "../lib/utils";

interface BaseCalendarProperties {
  className?: string;
}

type CalendarProperties = ComponentProps<typeof CalendarRac> &
  BaseCalendarProperties;
type RangeCalendarProperties = ComponentProps<typeof RangeCalendarRac> &
  BaseCalendarProperties;

const CalendarHeader = () => (
  <header className="flex w-full items-center gap-1 pb-1">
    <Button
      slot="previous"
      className="flex size-9 items-center justify-center rounded-lg text-gray-500 outline-offset-2 transition-colors hover:bg-strategy-gold/10 hover:text-boldness focus:outline-none data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-strategy-gold/70"
    >
      <ChevronLeft className="h-4 w-4" strokeWidth={2} />
    </Button>
    <HeadingRac className="grow text-center text-sm font-semibold text-boldness uppercase" />
    <Button
      slot="next"
      className="flex size-9 items-center justify-center rounded-lg text-gray-500 outline-offset-2 transition-colors hover:bg-strategy-gold/10 hover:text-boldness focus:outline-none data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-strategy-gold/70"
    >
      <ChevronRight className="h-4 w-4" strokeWidth={2} />
    </Button>
  </header>
);

const CalendarGridComponent = ({ isRange = false }: { isRange?: boolean }) => {
  const now = today(getLocalTimeZone());

  return (
    <CalendarGridRac className="w-full table-fixed">
      <CalendarGridHeaderRac className="w-full">
        {(day) => (
          <CalendarHeaderCellRac className="w-[14.28%] rounded-lg p-0 text-xs font-semibold text-gray-500 uppercase text-center">
            {day}
          </CalendarHeaderCellRac>
        )}
      </CalendarGridHeaderRac>
      <CalendarGridBodyRac className="w-full [&_td]:px-0 [&_td]:w-[14.28%]">
        {(date) => (
          <CalendarCellRac
            date={date}
            className={cn(
              "cursor-pointer relative flex w-full aspect-square items-center justify-center whitespace-nowrap rounded-lg border border-transparent p-0 text-sm font-normal text-boldness outline-offset-2 duration-150 [transition-property:color,background-color,border-radius,box-shadow] focus:outline-none data-[disabled]:pointer-events-none data-[unavailable]:pointer-events-none data-[focus-visible]:z-10 data-[hovered]:bg-strategy-gold/10 data-[selected]:bg-strategy-gold data-[selected]:font-extrabold data-[selected]:text-base data-[hovered]:text-boldness data-[selected]:text-boldness data-[unavailable]:line-through data-[disabled]:opacity-30 data-[unavailable]:opacity-30 data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-strategy-gold/70",
              // Range-specific styles
              isRange &&
                "data-[selected]:rounded-none data-[selection-end]:rounded-e-lg data-[selection-start]:rounded-s-lg data-[invalid]:bg-red-100 data-[selected]:bg-strategy-gold/20 data-[selected]:text-boldness data-[invalid]:data-[selection-end]:[&:not([data-hover])]:bg-red-500 data-[invalid]:data-[selection-start]:[&:not([data-hover])]:bg-red-500 data-[selection-end]:[&:not([data-hover])]:bg-strategy-gold data-[selection-start]:[&:not([data-hover])]:bg-strategy-gold data-[invalid]:data-[selection-end]:[&:not([data-hover])]:text-white data-[invalid]:data-[selection-start]:[&:not([data-hover])]:text-white data-[selection-end]:[&:not([data-hover])]:text-boldness data-[selection-start]:[&:not([data-hover])]:text-boldness",
              // Today indicator styles
              date.compare(now) === 0 &&
                cn(
                  "after:pointer-events-none after:absolute after:bottom-1 after:start-1/2 after:z-10 after:size-[3px] after:-translate-x-1/2 after:rounded-full after:bg-strategy-gold font-semibold",
                  isRange
                    ? "data-[selection-end]:[&:not([data-hover])]:after:bg-boldness data-[selection-start]:[&:not([data-hover])]:after:bg-boldness"
                    : "data-[selected]:after:bg-boldness",
                ),
            )}
          />
        )}
      </CalendarGridBodyRac>
    </CalendarGridRac>
  );
};

const Calendar = ({ className, ...properties }: CalendarProperties) => {
  return (
    <CalendarRac
      {...properties}
      className={composeRenderProps(className, (className) =>
        cn("w-full", className),
      )}
    >
      <CalendarHeader />
      <CalendarGridComponent />
    </CalendarRac>
  );
};

const RangeCalendar = ({
  className,
  ...properties
}: RangeCalendarProperties) => {
  return (
    <RangeCalendarRac
      {...properties}
      className={composeRenderProps(className, (className) =>
        cn("w-full", className),
      )}
    >
      <CalendarHeader />
      <CalendarGridComponent isRange />
    </RangeCalendarRac>
  );
};

export { Calendar, RangeCalendar };
